import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { formatApiError } from "../../lib/api";
import { listKnowledgeArticles, createKnowledgeArticle, updateKnowledgeArticle, myAgentProfile } from "../../lib/supportWorkspace";

export default function SupportWorkspaceKnowledge() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canAuthor, setCanAuthor] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState("both");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setArticles(await listKnowledgeArticles());
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    myAgentProfile().then((p) => setCanAuthor((p.capabilities || []).includes("knowledge_suggestion"))).catch(() => {});
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) return;
    try {
      await createKnowledgeArticle({ title, body_markdown: body, audience });
      toast.success("Article created as a draft");
      setTitle(""); setBody(""); setShowNew(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const publish = async (id) => {
    try {
      await updateKnowledgeArticle(id, { status: "published" });
      toast.success("Article published");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="support-workspace-knowledge">
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-display text-xl font-semibold text-slate-900">Knowledge base</h1>
        {canAuthor && (
          <button data-testid="support-kb-new-toggle" onClick={() => setShowNew((v) => !v)} className="btn-brand px-4 py-1.5 text-sm">
            {showNew ? "Cancel" : "New article"}
          </button>
        )}
      </div>

      {showNew && (
        <form onSubmit={submit} className="rounded-xl border border-slate-200 bg-white p-5 mb-6 space-y-3">
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title"
                 data-testid="support-kb-title-input" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Body (markdown)" rows={5}
                    data-testid="support-kb-body-input" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          <select value={audience} onChange={(e) => setAudience(e.target.value)} data-testid="support-kb-audience-select"
                  className="border border-slate-300 rounded-lg px-2 py-1.5 text-sm">
            <option value="both">Renters &amp; businesses</option>
            <option value="renter">Renters only</option>
            <option value="business">Businesses only</option>
          </select>
          <button type="submit" data-testid="support-kb-submit" className="btn-brand px-4 py-1.5 text-sm">Save draft</button>
        </form>
      )}

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : articles.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
          No knowledge articles yet.
        </div>
      ) : (
        <div className="space-y-2">
          {articles.map((a) => (
            <div key={a.id} data-testid={`support-kb-row-${a.id}`} className="rounded-xl border border-slate-200 bg-white p-4 flex items-center justify-between gap-3">
              <div>
                <p className="font-medium text-slate-900">{a.title}</p>
                <p className="text-xs text-slate-500">{a.audience} · v{a.version} · {a.status}</p>
              </div>
              {canAuthor && a.status !== "published" && (
                <button onClick={() => publish(a.id)} data-testid={`support-kb-publish-${a.id}`}
                        className="text-sm px-3 py-1.5 rounded-lg border border-emerald-600 text-emerald-700 hover:bg-emerald-50">
                  Publish
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
